import {
  Alert,
  Chip,
  Grid,
  Paper,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { readCorpusStatistics, searchStories } from '../api/endpoints';
import type { Paginated, ReadingCorpusStatistics, ReadingStorySummary } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';
import { SortableCell } from '../components/SortableCell';
import { TableFilters } from '../components/TableFilters';
import { StatTile } from '../components/StatTile';
import { StoryDetailDialog } from '../components/StoryDetailDialog';
import { TableSkeletonRows } from '../components/TableSkeletonRows';
import { useSort } from '../components/useSort';

const percent = (value: number | null): string => (value === null ? '—' : `${Math.round(value * 100)} %`);

const day = (value: string | null): string =>
  value === null ? '—' : new Date(value).toLocaleDateString('sk-SK', { dateStyle: 'medium' });

/**
 * Accuracy is read as a warning below this. It is a threshold for *looking*, not a verdict: a hard story is
 * allowed to be hard, and the only thing the colour claims is that this row is worth opening.
 */
const LOW_ACCURACY = 0.4;

/** The columns the API will order by. It matches these against a fixed list; anything else is ignored. */
type SortKey = 'title' | 'level' | 'minAge' | 'questionCount' | 'status' | 'code';

const LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export const ReadingStoriesPage = () => {
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const sort = useSort<SortKey>('code', 'asc', () => setPage(0));
  const [level, setLevel] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [openStoryId, setOpenStoryId] = useState<string | null>(null);
  const { data, error, isLoading, refetch } = useQuery<Paginated<ReadingStorySummary>>({
    queryKey: ['stories', term, page, sort.key, sort.direction, level, status],
    queryFn: () =>
      searchStories(
        term,
        page + 1,
        sort.key,
        sort.direction,
        level === '' ? undefined : Number(level),
        status || undefined,
      ),
  });
  const statistics = useQuery<ReadingCorpusStatistics>({
    queryKey: ['story-statistics'],
    queryFn: readCorpusStatistics,
  });

  const items = data?.items ?? [];
  const totals = statistics.data?.totals;

  return (
    <>
      <PageHeader
        title="Príbehy"
        subtitle="Čítanie s porozumením je jediná oblasť, ktorá potrebuje písaný obsah — ostatné úlohy sa generujú. Kliknutím na riadok sa zobrazí text príbehu, jeho otázky a to, ako sa mu darí. Príbehy sa pridávajú cez POST /reading-stories; formulár na písanie priamo tu zatiaľ nie je."
      />

      <ErrorNotice error={error} onRetry={() => void refetch()} />
      <ErrorNotice error={statistics.error} onRetry={() => void statistics.refetch()} />

      {totals ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <StatTile
              label="Príbehy"
              value={totals.stories}
              hint={`${totals.published} publikovaných, ${totals.draft} rozpracovaných`}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatTile
              label="Otázky"
              value={totals.questions}
              hint={`${totals.averageQuestionsPerStory} na príbeh`}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatTile
              label="Priemerná dĺžka"
              value={`${totals.averageWordCount} slov`}
              hint="naprieč korpusom"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatTile
              label="Nikdy nenasadené"
              value={totals.neverServed}
              hint="publikované, ale engine ich nikdy nevybral"
            />
          </Grid>
        </Grid>
      ) : null}

      {statistics.data && statistics.data.neverServed.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Tieto publikované príbehy engine ešte nikdy nevybral —{' '}
          {statistics.data.neverServed.map((story) => `${story.title} (úroveň ${story.level})`).join(', ')}.
          Buď na tú úroveň zatiaľ nikto nedosiahol, alebo ich skladba tréningu nedokáže zvoliť.
        </Alert>
      ) : null}

      <Paper variant="outlined">
        <TableFilters>
          <TextField
            label="Hľadať názov"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            label="Úroveň"
            value={level}
            onChange={(event) => {
              setLevel(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">všetky</MenuItem>
            {LEVELS.map((one) => (
              <MenuItem key={one} value={String(one)}>
                {one}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Stav"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">všetky</MenuItem>
            <MenuItem value="published">published</MenuItem>
            <MenuItem value="draft">draft</MenuItem>
          </TextField>
        </TableFilters>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="title">
                  Názov
                </SortableCell>
                <SortableCell sort={sort} column="level" align="center">
                  Úroveň
                </SortableCell>
                <SortableCell sort={sort} column="minAge" align="center">
                  Vek
                </SortableCell>
                <TableCell align="right">Slová</TableCell>
                <SortableCell sort={sort} column="questionCount" align="right">
                  Otázky
                </SortableCell>
                <TableCell align="right">Nasadení</TableCell>
                <TableCell align="right">Úspešnosť</TableCell>
                <TableCell align="right">Naposledy</TableCell>
                <SortableCell sort={sort} column="status">
                  Stav
                </SortableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? <TableSkeletonRows columns={9} /> : null}
              {!isLoading &&
                items.map((story: ReadingStorySummary) => (
                  <TableRow
                    key={story.id}
                    hover
                    onClick={() => setOpenStoryId(story.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 560 }}>
                        {story.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', fontFamily: 'ui-monospace, monospace' }}
                      >
                        {story.code} · {story.locale}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {story.level}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {story.minAge}–{story.maxAge}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {story.wordCount}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {story.questionCount}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: story.timesServed === 0 ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {story.timesServed}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: story.accuracy !== null && story.accuracy < LOW_ACCURACY ? 600 : 400,
                        color:
                          story.accuracy === null
                            ? 'text.disabled'
                            : story.accuracy < LOW_ACCURACY
                              ? 'warning.main'
                              : 'text.primary',
                      }}
                    >
                      {percent(story.accuracy)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        whiteSpace: 'nowrap',
                        color: 'text.secondary',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {day(story.lastServedAt)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={story.status}
                        color={story.status === 'published' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && items.length === 0 ? (
          <EmptyState
            title={term ? 'Žiadny príbeh nezodpovedá hľadaniu' : 'Korpus je prázdny'}
            detail={
              term
                ? `Skúste iný názov než „${term}".`
                : 'Naplňte ho príkazom npm run command:seed-reading-content alebo cez POST /reading-stories.'
            }
          />
        ) : null}

        <TablePagination
          component="div"
          count={data?.total ?? 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={data?.pageSize ?? 25}
          rowsPerPageOptions={[data?.pageSize ?? 25]}
          labelRowsPerPage="Na stranu"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />
      </Paper>

      {statistics.data ? (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
              <Typography variant="overline" color="text.secondary">
                Pokrytie podľa úrovne
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Úroveň bez publikovaného príbehu je úroveň, ktorú engine nedokáže obslúžiť.
              </Typography>
              <Stack spacing={1}>
                {statistics.data.byLevel.map((row) => (
                  <Stack key={row.level} direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2">Úroveň {row.level}</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {row.published} z {row.stories} publikovaných · {row.timesServed}× nasadené
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
              <Typography variant="overline" color="text.secondary">
                Pokrytie podľa schopnosti
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Koľko otázok cvičí ktorú čitateľskú schopnosť a v koľkých príbehoch.
              </Typography>
              <Stack spacing={1}>
                {statistics.data.bySubskill.map((row) => (
                  <Stack key={row.subskillCode} direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2">{row.subskillName}</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {row.questions} otázok v {row.stories} príbehoch
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      <StoryDetailDialog storyId={openStoryId} onClose={() => setOpenStoryId(null)} />
    </>
  );
};
